import { CardStatus, CardHolderType } from 'constants/unit';
import { gatewayClient } from '../utils/gatewayClient';

export interface FetchFormaCardsResponse {
  data: Array<{
    cardId: string;
    cardType: string;
    cardStatus: CardStatus;
    cardBinType: string;
    cardCreatedAt: string;
    cardLastFour: string;
    cardExpirationDate: string;
    cardDesign: string;
    bankCardId?: string;
    cardHolderId: string;
    cardHolderType: CardHolderType;
    cardHolderReferenceId: string;
    cardHolderDetails: {
      fullName: {
        firstName: string;
        lastName: string;
      };
      billingAddress: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
      shippingAddress?: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
    };
  }>;
}

export const fetchFormaCards = async () =>
  gatewayClient.get<never, FetchFormaCardsResponse>(`/banking/v1/member/cards`);

export const updateFormaCardSettings = async ({
  address,
  card_id,
}: {
  address: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country?: string;
  };
  card_id?: string;
}) =>
  gatewayClient.patch(`/banking/v1/member/cards/${card_id}`, {
    address,
  });

export const freezeFormaCard = async ({ card_id }: { card_id: string }) =>
  gatewayClient.put(`/banking/v1/member/cards/${card_id}/freeze`);

export const unfreezeFormaCard = async ({ card_id }: { card_id: string }) =>
  gatewayClient.put(`/banking/v1/member/cards/${card_id}/unfreeze`);

export const replaceDamagedFormaCard = async ({
  shipping_address,
  card_id,
}: {
  shipping_address: {
    city: string;
    street: string;
    street2?: string;
    state: string;
    postalCode: string;
    country: string;
  };
  card_id?: string;
}) =>
  gatewayClient.post(`/banking/v1/member/cards/${card_id}/replace`, {
    shippingAddress: shipping_address,
  });

export const replaceLostFormaCard = async ({
  shipping_address,
  card_id,
}: {
  shipping_address: {
    city: string;
    street: string;
    street2?: string;
    state: string;
    postalCode: string;
    country: string;
  };
  card_id?: string;
}) =>
  gatewayClient.post(`/banking/v1/member/cards/${card_id}/lost`, {
    shippingAddress: shipping_address,
  });

export const replaceStolenFormaCard = async ({
  shipping_address,
  card_id,
}: {
  shipping_address: {
    city: string;
    street: string;
    street2?: string;
    state: string;
    postalCode: string;
    country: string;
  };
  card_id?: string;
}) =>
  gatewayClient.post(`/banking/v1/member/cards/${card_id}/stolen`, {
    shippingAddress: shipping_address,
  });

// Issue dependent cards - create both physical and virtual cards
export const createFormaCards = async ({
  card_details,
}: {
  card_details: {
    cardHolderType: string;
    cardHolderReferenceId: string;
    cardHolderDetails: {
      fullName: {
        firstName: string;
        lastName: string;
      };
      dateOfBirth?: string;
      address: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
      };
      shippingAddress: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
      };
      phone: {
        countryCode: string;
        phoneNumber: string;
      };
      email?: string;
    };
  };
}) => gatewayClient.post(`/banking/v1/member/cards?type=all`, card_details);

// Re-issue single virtual or physical cards
export const createFormaCard = async ({
  card_details,
  card_type,
}: {
  card_details: {
    cardHolderType: string;
    cardHolderReferenceId: string;
    cardHolderDetails: {
      fullName: {
        firstName: string;
        lastName: string;
      };
      dateOfBirth?: string;
      address: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
      };
      shippingAddress?: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        postalCode: string;
      };
      phone: {
        countryCode: string;
        phoneNumber: string;
      };
      email?: string;
    };
  };
  card_type: string;
}) =>
  gatewayClient.post(
    `/banking/v1/member/cards?type=${card_type}`,
    card_details
  );
